import {
  EVENT_DETAIL_PATH_PATTERN,
  REGISTRATION_TOKEN_FRAGMENT_PARAM,
  REGISTRATION_TOKEN_MAX_LENGTH,
  REGISTRATION_TOKEN_PATTERN,
} from './constants/guest-registration';

interface PendingRegistration {
  eventId: string;
  token: string;
}

/**
 * A token lifted off the address bar but not yet acted on. Module-level, so it
 * survives from `src/instrument.ts` (which runs before React and before Sentry
 * can record the page URL) to the event page's first render. Memory only, never
 * persisted here, and never logged.
 */
let pending: PendingRegistration | null = null;

/** Pure: the token a URL fragment (`#r=<token>`) carries, or null when absent or not token-shaped. */
export function parseRegistrationFragment(hash: string): string | null {
  const token = new URLSearchParams(hash.replace(/^#/, '')).get(REGISTRATION_TOKEN_FRAGMENT_PARAM);
  if (!token || token.length > REGISTRATION_TOKEN_MAX_LENGTH) return null;
  return REGISTRATION_TOKEN_PATTERN.test(token) ? token : null;
}

/** Pure: the event id of an `/e/:id` path, or null for any other path. */
export function parseEventIdFromPath(pathname: string): string | null {
  const match = EVENT_DETAIL_PATH_PATTERN.exec(pathname);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    // A malformed escape is not an event id this app would have produced.
    return null;
  }
}

/**
 * Take a `#r=<token>` fragment off the address bar on an `/e/:id` path (FR-286):
 * remember it in memory against that event and remove it from the address bar
 * at once. A fragment is never sent to a server, but it stays in the bar,
 * history and anything the visitor copies, and a page-load recorder (Sentry
 * tracing and replay) can capture the full URL, so it is stripped before
 * anything else runs. Other fragment parameters and the query are kept, and the
 * router's `history.state` is preserved.
 *
 * Does nothing on another path or when the fragment holds no token-shaped
 * value (the address is then left alone). Safe to call repeatedly: once the
 * fragment is gone there is nothing left to take. Writes to the address bar, so
 * call it from startup code or an effect, never while rendering.
 */
export function captureRegistrationFragment(): void {
  const { hash, pathname, search } = window.location;
  const eventId = parseEventIdFromPath(pathname);
  const token = eventId === null ? null : parseRegistrationFragment(hash);
  if (eventId === null || token === null) return;

  pending = { eventId, token };
  const rest = new URLSearchParams(hash.replace(/^#/, ''));
  rest.delete(REGISTRATION_TOKEN_FRAGMENT_PARAM);
  const remaining = rest.toString();
  window.history.replaceState(window.history.state, '', `${pathname}${search}${remaining ? `#${remaining}` : ''}`);
}

/**
 * The token waiting for this event, without consuming it: the one already
 * captured, else one still in the address bar. Pure read, safe while rendering,
 * so the first render can already show a registered visitor as registered.
 */
export function peekPendingRegistrationToken(eventId: string): string | null {
  if (pending?.eventId === eventId) return pending.token;
  const { hash, pathname } = window.location;
  return parseEventIdFromPath(pathname) === eventId ? parseRegistrationFragment(hash) : null;
}

/**
 * Consume the token waiting for this event: capture anything still in the
 * address bar, then return it and forget it. Null when none is waiting for this
 * event (a token held for another event is left alone). Call from an effect.
 */
export function takePendingRegistrationToken(eventId: string): string | null {
  captureRegistrationFragment();
  if (pending?.eventId !== eventId) return null;
  const { token } = pending;
  pending = null;
  return token;
}

/** Test seam: drops the in-memory token between specs. */
export function resetPendingRegistration(): void {
  pending = null;
}
